import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
/**
 * The audit trail.
 *
 * Every mutating action in this console writes an `AuditLog` row inside the
 * same transaction as the change (design spec §6), so this screen is not a
 * best-effort activity feed — it is the record, and a mutation that is missing
 * from it did not happen.
 *
 * Two vocabularies live in the table, and the screen shows both rather than
 * hiding the seam:
 *
 * - **Console actions** are the design's dotted verbs — `food.verify`,
 *   `health.reveal`. Listed below, and `admin-audit-actions.ts` on the API side
 *   is checked against this list.
 * - **App actions** are SCREAMING_SNAKE and predate the console —
 *   `AUTH_LOGIN`, `SUBSCRIPTION_UPDATED`, `ONBOARDING_COMPLETED`. They are
 *   written by the product itself, not by an operator.
 *
 * The action filter is therefore a free string, not an enum: the table already
 * holds names this build has never heard of, and it will hold more.
 */
export const ADMIN_CONSOLE_ACTIONS = [
    'health.reveal',
    'user.return_decision.record',
    'user.plan_safety_hold.lift',
    'user.entitlement.grant',
    'user.entitlement.revoke',
    'food.create',
    'food.verify',
    'food.deactivate',
    'food.reactivate',
    'food.merge',
    'food.update',
    'food.portion.create',
    'food.portion.update',
    'food.portion.delete',
    /** One name a food answers to, added or taken off on its own. */
    'food.alias.create',
    'food.alias.delete',
    'food.recipe.update',
    'food.recipe.derive',
    'exercise.create',
    'exercise.update',
    'exercise.delete',
    'exercise.deactivate',
    'exercise.reactivate',
    /** A curated alternative, added or removed from the console. */
    'exercise.edge.create',
    'exercise.edge.delete',
    'exercise.media.upload',
    'exercise.media.remove',
    'exercise.images.upload',
    'exercise.images.remove',
    /**
     * Steado's pick: the YouTube video an operator chose for an exercise, shown
     * in the app beside the search results.
     */
    'exercise.video_pin.set',
    'exercise.video_pin.remove',
    'job.retry',
    'job.discard',
    'billing.replay',
    'promo.create',
    'promo.update',
    'promo.archive',
    'plan.regenerate',
    'notification.delivery.retry',
    'notification.delivery.suppress',
    'notification.control.enable',
    'notification.control.disable',
    'feature_switch.enable',
    'feature_switch.disable',
    'notification.broadcast.create',
    'notification.broadcast.cancel',
    'notification.broadcast.image_upload',
    'meal_pattern.create',
    'meal_pattern.update',
    'meal_pattern.activate',
    'meal_pattern.retire',
    /**
     * An operator answering "I could not find my food". Two verbs rather than one
     * with a field, because the two answers are not the same kind of act: one
     * points at a catalog row, the other closes the request with a reason.
     */
    'food_request.resolve',
    'food_request.reject',
    /**
     * The rest of a food request's answers: AI research queued by an operator
     * (with their note), an approval (a new food, a name or a fix, written
     * through the catalog engine), an AI estimate kept as it is, and an answer
     * taken back once its catalog change was reverted.
     */
    'food_request.research',
    'food_request.approve',
    'food_request.keep_estimate',
    'food_request.reopen',
    /**
     * The copy shown while a plan generates. No delete verb: a tip is retired
     * with `isActive: false`, which is an update.
     */
    'content.wait_tip.create',
    'content.wait_tip.update',
    /**
     * Answering "Send feedback". Both close the report; `resolve` says an
     * operator acted on it or confirmed there's nothing to do, `dismiss` says
     * it wasn't actionable (a duplicate, out of scope, not enough to go on).
     */
    'feedback.resolve',
    'feedback.dismiss',
    /**
     * "From gyms" (Steado for Gyms): a group of gym foods or exercises linked to
     * the catalog row that now stands for them, or kept the gym's own, and a
     * gym's report on a catalog food answered.
     */
    'gym_catalog.link',
    'gym_catalog.keep',
    'gym_report.resolve',
    'gym_report.reject',
    /**
     * Exercises equipment scans found missing from the catalog: an AI draft
     * asked for, the request pointed at a catalog row (new or existing) or
     * declined, and a scan's photo viewed. The photo can show other people at
     * the gym, so every view is recorded, with whose scan it was.
     */
    'exercise_request.draft',
    'exercise_request.resolve',
    'exercise_request.reject',
    'exercise_request.sample_view',
    /**
     * The catalog's history: a pushed batch applied, a batch put back, and one
     * change put back. The row-level record of each is `catalog_changes`; these
     * say who pressed the button.
     */
    'catalog.batch.apply',
    'catalog.batch.revert',
    'catalog.change.revert',
    /**
     * An admin setting or clearing a gym's AI limit overrides (monthly tokens,
     * Ask tokens per person per day, AI drafts per member per week). The
     * metadata holds each limit before and after (null is the default).
     */
    'gym.ai_limits.update',
    /**
     * An operator marking a trainer's or an owner's account deletion request
     * done, after deleting the account (or finding it already gone).
     */
    'gym.deletion_request.done',
    /**
     * An admin creating a gym and its owner (owners cannot sign themselves up),
     * and sending the owner's invite again. The metadata names the owner.
     */
    'gym.create',
    'gym.owner_invite.resend',
    /** An admin changing how many members a gym may have; the metadata has the limit before and after. */
    'gym.member_limit.update',
];
/** The entities console actions are recorded against. */
export const AUDIT_ENTITIES = [
    'user',
    'food',
    'exercise',
    'job',
    'subscription_event',
    'plan_week',
    'notification_delivery',
    'notification_control',
    'feature_switch',
    'admin_broadcast',
    'meal_pattern',
    'food_request',
    'wait_tip',
    'feedback',
    'exercise_request',
    'catalog_batch',
    'catalog_change',
    /** A gym, as the product's own audit rows name it (hence the capital). */
    'Gym',
    'gym_account_deletion_request',
];
export const adminAuditEntrySchema = z.object({
    id: z.string().uuid(),
    action: z.string(),
    /** True when this is one of the console's own dotted verbs. */
    isConsoleAction: z.boolean(),
    entity: z.string(),
    entityId: z.string().nullable(),
    /**
     * Null for a system-initiated action, and for one whose actor has since been
     * deleted — `AuditLog.actor` is SetNull, because losing the whole record with
     * the account would be worse than losing the name on it.
     */
    actorUserId: z.string().uuid().nullable(),
    actorName: z.string().nullable(),
    ip: z.string().nullable(),
    /** Whatever the writer recorded. Shape varies per action, deliberately. */
    metadata: z.unknown(),
    createdAt: z.string(),
});
export const AUDIT_SCOPES = ['console', 'app'];
export const adminActivityQuerySchema = cursorPageQuerySchema.extend({
    /** Exact match. A free string, because the vocabulary is open. */
    action: z.string().trim().max(64).optional(),
    entity: z.string().trim().max(64).optional(),
    entityId: z.string().trim().max(64).optional(),
    actorUserId: z.string().uuid().optional(),
    /**
     * `console` narrows to the operator actions; `app` to everything the product
     * wrote itself. Absent means both — an operator investigating an account
     * usually wants the whole story, not one half of it.
     */
    scope: z.enum(AUDIT_SCOPES).optional(),
    /** Inclusive ISO bounds. Both optional; either alone is a half-open range. */
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
});
//# sourceMappingURL=activity.js.map